import { client } from '../../..'
import { REMOVE_EMPLOYEE_TAG, ADD_EMPLOYEE_TAG, ADD_ENTRY_LEVEL_LOW_TAG } from '../Mutations'
import { EMPLOYEE_HAS_ONGOING_APPRAISAL, EMPLOYMENT_STATUS_OF_EMPLOYEE } from '../Queries'
import {
  RemoveEmployeeTagMutationVariables,
  RemoveEmployeeTagMutation,
  RemoveEmployeeTagInput,
  EmployeeTagType,
  AddEmployeeTagMutation,
  AddEmployeeTagMutationVariables,
  Country,
  EmployeeHasOngoingAppraisalQuery,
  EmployeeHasOngoingAppraisalQueryVariables,
  EmploymentStatusOfEmployeeQuery,
  EmploymentStatusOfEmployeeQueryVariables,
  AddEntryLevelLowTagMutation,
  AddEntryLevelLowTagMutationVariables,
} from '../../../types'
import { EMPLOYEES_AND_TAGS, TAG_HISTORY_OF_EMPLOYEE_UNTIL_REPLY_ENTRY_DATE } from '../Queries'
import { LIVE_BOARD_OR_HISTORY } from '../../talents-season/Queries'
import { ApolloQueryResult, FetchResult, MaybeMasked } from '@apollo/client'

export default (
  companyId: string,
  country: Country,
  year: number | undefined,
  rankingInProgress: boolean | undefined,
): {
  removeEmployeeTag: (removeEmployeeTagInput: RemoveEmployeeTagInput) => Promise<FetchResult<MaybeMasked<RemoveEmployeeTagMutation>>>
  addEmployeeTag: (employeeId: string, type: EmployeeTagType) => Promise<FetchResult<MaybeMasked<AddEmployeeTagMutation>>>
  addEntryLevelLowTag: (employeeId: string) => Promise<FetchResult<MaybeMasked<AddEntryLevelLowTagMutation>>>
  employeeHasOngoingAppraisal: (employeeId: string) => Promise<ApolloQueryResult<MaybeMasked<EmployeeHasOngoingAppraisalQuery>>>
  employeeHasFutureEmploymentChange: (employeeId: string) => Promise<ApolloQueryResult<MaybeMasked<EmploymentStatusOfEmployeeQuery>>>
} => {
  const employeeQuery = rankingInProgress
    ? {
        query: LIVE_BOARD_OR_HISTORY,
        variables: {
          companyId,
          country,
          year,
        },
      }
    : {
        query: EMPLOYEES_AND_TAGS,
        variables: {
          companyId,
          country,
        },
      }

  const removeEmployeeTag = async (
    removeEmployeeTagInput: RemoveEmployeeTagInput,
  ): Promise<FetchResult<MaybeMasked<RemoveEmployeeTagMutation>>> => {
    return client.mutate<RemoveEmployeeTagMutation, RemoveEmployeeTagMutationVariables>({
      mutation: REMOVE_EMPLOYEE_TAG,
      variables: {
        removeEmployeeTagInput,
      },
      refetchQueries: [
        employeeQuery,
        {
          query: TAG_HISTORY_OF_EMPLOYEE_UNTIL_REPLY_ENTRY_DATE,
          variables: {
            employeeId: removeEmployeeTagInput.employeeId,
          },
        },
      ],
    })
  }

  const addEmployeeTag = async (employeeId: string, type: EmployeeTagType): Promise<FetchResult<MaybeMasked<AddEmployeeTagMutation>>> => {
    return await client.mutate<AddEmployeeTagMutation, AddEmployeeTagMutationVariables>({
      mutation: ADD_EMPLOYEE_TAG,
      variables: {
        employeeId: employeeId,
        type: type,
      },
      refetchQueries: [
        employeeQuery,
        {
          query: TAG_HISTORY_OF_EMPLOYEE_UNTIL_REPLY_ENTRY_DATE,
          variables: {
            employeeId,
          },
        },
      ],
    })
  }

  const addEntryLevelLowTag = async (employeeId: string): Promise<FetchResult<MaybeMasked<AddEntryLevelLowTagMutation>>> => {
    return await client.mutate<AddEntryLevelLowTagMutation, AddEntryLevelLowTagMutationVariables>({
      mutation: ADD_ENTRY_LEVEL_LOW_TAG,
      variables: {
        employeeId: employeeId,
      },
      refetchQueries: [
        employeeQuery,
        {
          query: TAG_HISTORY_OF_EMPLOYEE_UNTIL_REPLY_ENTRY_DATE,
          variables: {
            employeeId,
          },
        },
      ],
    })
  }

  const employeeHasOngoingAppraisal = async (
    employeeId: string,
  ): Promise<ApolloQueryResult<MaybeMasked<EmployeeHasOngoingAppraisalQuery>>> => {
    return client.query<EmployeeHasOngoingAppraisalQuery, EmployeeHasOngoingAppraisalQueryVariables>({
      query: EMPLOYEE_HAS_ONGOING_APPRAISAL,
      variables: {
        employeeId: employeeId,
      },
      fetchPolicy: 'network-only',
    })
  }

  const employeeHasFutureEmploymentChange = async (
    employeeId: string,
  ): Promise<ApolloQueryResult<MaybeMasked<EmploymentStatusOfEmployeeQuery>>> => {
    return client.query<EmploymentStatusOfEmployeeQuery, EmploymentStatusOfEmployeeQueryVariables>({
      query: EMPLOYMENT_STATUS_OF_EMPLOYEE,
      variables: {
        employeeId: employeeId,
      },
      fetchPolicy: 'network-only',
    })
  }

  return { removeEmployeeTag, addEmployeeTag, addEntryLevelLowTag, employeeHasOngoingAppraisal, employeeHasFutureEmploymentChange }
}
